/**
 * L'éditeur de texte riche (TipTap), et la barre d'outils qui va avec.
 *
 * ## Ce qui remplace quoi
 *
 * La phase 3 avait livré un éditeur « par blocs » — une liste de zones de texte
 * typées — faute de pouvoir installer TipTap dans cet environnement. Le format
 * produit était déjà exactement celui-ci, si bien que ce remplacement ne touche
 * ni le backend, ni le contrat OpenAPI, ni un seul document déjà enregistré :
 * l'écart assumé de `HANDOFF.md` se referme sur un seul composant.
 *
 * ## Non contrôlé, et pourquoi
 *
 * `content` n'est lu qu'à la création de l'éditeur, puis à chaque fois que
 * l'identité du document change (`documentKey`). Repousser à chaque frappe le
 * document que l'on vient d'émettre ferait sauter le curseur en fin de champ à
 * chaque caractère — la faute classique d'un éditeur riche branché comme une
 * entrée contrôlée. Le parent reçoit les modifications par `onChange` et n'a
 * rien à renvoyer.
 *
 * ## Ce qui sort
 *
 * `onChange` rend la forme **canonique** (`toStored`) : les attributs nuls que
 * TipTap sérialise sont retirés, et un document vidé vaut `null`. C'est ce que
 * le serveur garde, donc ce que le prochain chargement relira — sans quoi
 * l'éditeur se croirait modifié dès son ouverture.
 */
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import { useEffect, useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/cn";
import { asDocument, toStored, type ContentDocument } from "@/shared/richtext/document";
import {
  HEADING_LEVELS,
  TEXT_ALIGNMENTS,
  richTextExtensions,
  type TextAlignment,
} from "@/shared/richtext/extensions";

/**
 * Le tableau que le bouton « Tableau » pose : trois colonnes, une ligne
 * d'en-tête et deux lignes de corps.
 *
 * Assez petit pour tenir dans la largeur d'un champ de prose, et assez grand
 * pour qu'on voie **où** taper — un tableau 1×1 ressemble à un cadre autour
 * d'un paragraphe, et l'auteur ne devine pas qu'il doit ajouter des colonnes.
 */
const TABLE_ROWS = 3;
const TABLE_COLUMNS = 3;

/** Ce que l'éditeur porte, sous la forme que le serveur garde. */
function read(instance: Editor): ContentDocument | null {
  return toStored(asDocument(instance.getJSON() as Record<string, unknown>));
}

interface RichTextEditorProps {
  /**
   * Libellé du champ. Affiché **et** porté par `aria-label` sur la zone
   * éditable : un `<label htmlFor>` ne peut pas désigner un `contenteditable`,
   * qui n'est pas un contrôle de formulaire.
   */
  label: string;
  /** Masque le libellé visible, quand l'écran en pose déjà un à côté. */
  hideLabel?: boolean;
  content: ContentDocument | null | undefined;
  /** À chaque frappe. Facultatif : un écran qui n'enregistre qu'à la sortie du
   *  champ n'a rien à faire de l'état intermédiaire. */
  onChange?: (document: ContentDocument | null) => void;
  /**
   * Appelé quand le champ perd le focus, avec le document tel qu'il est alors.
   *
   * Pour les écrans qui enregistrent au fil de l'eau plutôt que sur un bouton :
   * brancher une mutation sur `onChange` enverrait une requête **par frappe**,
   * et ProseMirror émet une transaction au chargement — donc une écriture que
   * personne n'a demandée, sur une page qui vient de s'ouvrir.
   */
  onBlur?: (document: ContentDocument | null) => void;
  /**
   * Change quand le parent charge **un autre** document (un autre cours, une
   * autre leçon). L'éditeur se recharge alors ; sans cette clé, passer d'une
   * leçon à l'autre laisserait le texte de la précédente à l'écran.
   */
  documentKey?: string;
  placeholder?: string;
  hint?: ReactNode;
  disabled?: boolean;
  /** Hauteur minimale de la zone de saisie, en classes Tailwind. */
  minHeight?: string;
}

export function RichTextEditor({
  label,
  content,
  onChange,
  onBlur,
  documentKey,
  placeholder,
  hint,
  disabled = false,
  hideLabel = false,
  minHeight = "min-h-40",
}: RichTextEditorProps) {
  const { t } = useTranslation("common");
  const fieldId = useId();
  const hintId = `${fieldId}-hint`;

  const editor = useEditor(
    {
      extensions: richTextExtensions(placeholder ?? t("richText.placeholder")),
      content: content ?? undefined,
      editable: !disabled,
      editorProps: {
        attributes: {
          class: cn(
            "prose-teach w-full rounded-b-md px-3 py-2 text-sm focus:outline-none",
            minHeight,
          ),
          // `role` et `aria-multiline` posés explicitement : un
          // `contenteditable` a bien le rôle `textbox` dans la spécification,
          // mais rien ne le garantit d'un lecteur d'écran à l'autre — et le
          // laisser implicite rendrait le champ introuvable par son libellé.
          role: "textbox",
          "aria-multiline": "true",
          "aria-label": label,
          ...(hint === undefined ? {} : { "aria-describedby": hintId }),
        },
      },
      onUpdate: ({ editor: instance }) => {
        onChange?.(read(instance));
      },
      onBlur: ({ editor: instance }) => {
        onBlur?.(read(instance));
      },
    },
    // Recréé seulement quand le document change d'identité. `disabled` y figure
    // aussi : `editable` est lu à la construction.
    [documentKey, disabled],
  );

  // `setEditable` plutôt qu'une recréation quand seul le droit d'écrire change
  // en cours de vie — recréer viderait l'historique d'annulation.
  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return (
    <div className="space-y-1.5">
      <span aria-hidden="true" className={cn("block text-sm font-medium", hideLabel && "sr-only")}>
        {label}
      </span>
      <div
        className={cn(
          "rounded-md border border-slate-300 bg-white",
          "focus-within:border-brand-500 focus-within:ring-brand-500 focus-within:ring-1",
          "dark:border-slate-700 dark:bg-slate-900",
          disabled && "opacity-50",
        )}
      >
        {!disabled && <Toolbar editor={editor} />}
        <EditorContent editor={editor} />
      </div>
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * La barre d'outils.
 *
 * Chaque bouton porte `aria-pressed` : sans lui, un lecteur d'écran annonce
 * « bouton Gras » sans jamais dire si le texte sélectionné l'est déjà (docs/24
 * §3). Et chaque bouton est `type="button"` — dans un formulaire, le défaut
 * `submit` enverrait la page à la première mise en gras.
 */
function Toolbar({ editor }: { editor: Editor }) {
  const { t } = useTranslation("common");

  // ⚠️ TipTap v3 ne re-rend **plus** à chaque transaction : `useEditor` seul
  // laisserait cette barre afficher l'état de la sélection précédente, donc un
  // bouton « Gras » qui ne s'allume jamais. `useEditorState` s'abonne à ce que
  // la barre lit vraiment, et rien de plus — le défaut est silencieux, et se
  // constate en cliquant, pas en lisant le code.
  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance.isActive("bold"),
      italic: instance.isActive("italic"),
      underline: instance.isActive("underline"),
      strike: instance.isActive("strike"),
      code: instance.isActive("code"),
      bulletList: instance.isActive("bulletList"),
      orderedList: instance.isActive("orderedList"),
      blockquote: instance.isActive("blockquote"),
      codeBlock: instance.isActive("codeBlock"),
      link: instance.isActive("link"),
      headings: HEADING_LEVELS.map((level) => instance.isActive("heading", { level })),
      alignments: TEXT_ALIGNMENTS.map((alignment) => instance.isActive({ textAlign: alignment })),
      canUndo: instance.can().undo(),
      canRedo: instance.can().redo(),
      // Le groupe « tableau » n'est monté que dans un tableau : neuf boutons
      // grisés en permanence pour les champs qui n'en portent jamais — une
      // biographie, l'appréciation d'une copie — coûteraient plus de barre que
      // de service.
      inTable: instance.isActive("table"),
      canMergeOrSplit: instance.can().mergeOrSplit(),
    }),
  });

  return (
    <div
      role="toolbar"
      aria-label={t("richText.toolbar")}
      className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 p-1 dark:border-slate-800"
    >
      <ToolbarButton
        label={t("richText.bold")}
        active={state.bold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <span className="font-bold">{GLYPHS.bold}</span>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.italic")}
        active={state.italic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="italic">{GLYPHS.italic}</span>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.underline")}
        active={state.underline}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <span className="underline">{GLYPHS.underline}</span>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.strike")}
        active={state.strike}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <span className="line-through">{GLYPHS.strike}</span>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.code")}
        active={state.code}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <span className="font-mono">{GLYPHS.code}</span>
      </ToolbarButton>

      <Separator />

      {HEADING_LEVELS.map((level, index) => (
        <ToolbarButton
          key={level}
          label={t("richText.heading", { level })}
          active={state.headings[index] ?? false}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
        >
          <Glyph>H{level}</Glyph>
        </ToolbarButton>
      ))}

      <Separator />

      <ToolbarButton
        label={t("richText.bulletList")}
        active={state.bulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <Glyph>{GLYPHS.bulletList}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.orderedList")}
        active={state.orderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <Glyph>{GLYPHS.orderedList}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.blockquote")}
        active={state.blockquote}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Glyph>{GLYPHS.blockquote}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.codeBlock")}
        active={state.codeBlock}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <span className="font-mono text-xs">{GLYPHS.codeBlock}</span>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.horizontalRule")}
        active={false}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Glyph>{GLYPHS.horizontalRule}</Glyph>
      </ToolbarButton>

      <Separator />

      {TEXT_ALIGNMENTS.map((alignment, index) => (
        <ToolbarButton
          key={alignment}
          label={t(`richText.align.${alignment}`)}
          active={state.alignments[index] ?? false}
          onClick={() => editor.chain().focus().setTextAlign(alignment).run()}
        >
          <Glyph>{GLYPHS[alignment]}</Glyph>
        </ToolbarButton>
      ))}

      <Separator />

      <LinkButton editor={editor} active={state.link} />
      {/* Grisé dans un tableau : le schéma ProseMirror accepte un tableau dans
          une cellule, et l'auteur qui reclique n'a jamais voulu ça. Un
          `aria-pressed` n'aurait rien dit ici — insérer est une action, pas un
          état. */}
      <ToolbarButton
        label={t("richText.table.insert")}
        active={false}
        disabled={state.inTable}
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: TABLE_ROWS, cols: TABLE_COLUMNS, withHeaderRow: true })
            .run()
        }
      >
        <Glyph>{GLYPHS.table}</Glyph>
      </ToolbarButton>

      {state.inTable && <TableControls editor={editor} canMergeOrSplit={state.canMergeOrSplit} />}

      <Separator />

      <ToolbarButton
        label={t("richText.undo")}
        active={false}
        disabled={!state.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Glyph>{GLYPHS.undo}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.redo")}
        active={false}
        disabled={!state.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Glyph>{GLYPHS.redo}</Glyph>
      </ToolbarButton>
    </div>
  );
}

/**
 * Les gestes qui n'ont de sens que **dans** un tableau.
 *
 * Ils ne sont montés que là, et c'est ce qui les rend lisibles : la barre est
 * partagée par tous les champs de prose de la plateforme, dont beaucoup ne
 * porteront jamais de tableau.
 *
 * `active={false}` partout : ce sont des actions, pas des états. `aria-pressed`
 * à « faux » sur « supprimer la ligne » annoncerait un interrupteur éteint là
 * où il n'y a qu'un geste — mais le laisser absent rendrait ces boutons
 * différents des autres pour rien. Le compromis vaut d'être écrit : le groupe
 * n'apparaît que lorsque le geste est possible, donc l'état qu'il annonce est
 * toujours le même.
 */
function TableControls({ editor, canMergeOrSplit }: { editor: Editor; canMergeOrSplit: boolean }) {
  const { t } = useTranslation("common");

  return (
    <>
      <Separator />
      <ToolbarButton
        label={t("richText.table.addRowBefore")}
        active={false}
        onClick={() => editor.chain().focus().addRowBefore().run()}
      >
        <Glyph>{GLYPHS.rowBefore}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.addRowAfter")}
        active={false}
        onClick={() => editor.chain().focus().addRowAfter().run()}
      >
        <Glyph>{GLYPHS.rowAfter}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.deleteRow")}
        active={false}
        onClick={() => editor.chain().focus().deleteRow().run()}
      >
        <Glyph>{GLYPHS.rowDelete}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.addColumnBefore")}
        active={false}
        onClick={() => editor.chain().focus().addColumnBefore().run()}
      >
        <Glyph>{GLYPHS.columnBefore}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.addColumnAfter")}
        active={false}
        onClick={() => editor.chain().focus().addColumnAfter().run()}
      >
        <Glyph>{GLYPHS.columnAfter}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.deleteColumn")}
        active={false}
        onClick={() => editor.chain().focus().deleteColumn().run()}
      >
        <Glyph>{GLYPHS.columnDelete}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.toggleHeaderRow")}
        active={false}
        onClick={() => editor.chain().focus().toggleHeaderRow().run()}
      >
        <Glyph>{GLYPHS.headerRow}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.mergeOrSplit")}
        active={false}
        disabled={!canMergeOrSplit}
        onClick={() => editor.chain().focus().mergeOrSplit().run()}
      >
        <Glyph>{GLYPHS.mergeOrSplit}</Glyph>
      </ToolbarButton>
      <ToolbarButton
        label={t("richText.table.delete")}
        active={false}
        onClick={() => editor.chain().focus().deleteTable().run()}
      >
        <Glyph>{GLYPHS.tableDelete}</Glyph>
      </ToolbarButton>
    </>
  );
}

/**
 * Poser ou retirer un lien.
 *
 * `window.prompt` plutôt qu'une boîte de dialogue maison : l'invite native est
 * annoncée par les lecteurs d'écran, se ferme à l'échappement et ne demande
 * aucune gestion de focus. Un dialogue riche viendra quand l'écran en aura
 * besoin pour autre chose qu'une URL.
 *
 * L'URL n'est pas validée ici : `ALLOWED_LINK_SCHEMES` est la garantie, et elle
 * est côté serveur. TipTap refuse déjà tout ce qui n'est pas `http(s)` ou
 * `mailto` (`protocols`), ce qui évite de composer un lien que
 * l'enregistrement rejettera — mais c'est un service rendu, pas une garde.
 */
function LinkButton({ editor, active }: { editor: Editor; active: boolean }) {
  const { t } = useTranslation("common");

  return (
    <ToolbarButton
      label={active ? t("richText.unlink") : t("richText.link")}
      active={active}
      onClick={() => {
        if (active) {
          editor.chain().focus().unsetLink().run();
          return;
        }
        const previous: unknown = editor.getAttributes("link")["href"];
        const href = window.prompt(
          t("richText.linkPrompt"),
          typeof previous === "string" ? previous : "https://",
        );
        if (href === null || href.trim() === "") {
          return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: href.trim() }).run();
      }}
    >
      🔗
    </ToolbarButton>
  );
}

/**
 * Un glyphe de barre d'outils.
 *
 * `aria-hidden` parce qu'il ne se lit pas : « ❝ » n'a pas de prononciation, et
 * c'est `aria-label` qui porte le nom du bouton. C'est aussi ce qui le sort
 * légitimement de l'i18n — un pictogramme ne se traduit pas, et le mettre dans
 * le bundle laisserait un traducteur remplacer une flèche par un mot.
 */
function Glyph({ children }: { children: ReactNode }) {
  return <span aria-hidden="true">{children}</span>;
}

/**
 * Les pictogrammes, rassemblés.
 *
 * Écrits ici et non dans le JSX pour une raison qui vaut d'être dite : ce sont
 * les seules chaînes visibles de l'application qui échappent légitimement à
 * l'i18n (règle 10), et les tenir en un endroit rend cette exception lisible
 * plutôt que dispersée en autant de dérogations.
 */
const GLYPHS = {
  bold: "B",
  italic: "I",
  underline: "U",
  strike: "S",
  code: "<>",
  bulletList: "•",
  orderedList: "1.",
  blockquote: "❝",
  codeBlock: "{ }",
  horizontalRule: "—",
  link: "🔗",
  table: "⊞",
  tableDelete: "⊠",
  rowBefore: "↑+",
  rowAfter: "↓+",
  rowDelete: "↕−",
  columnBefore: "←+",
  columnAfter: "→+",
  columnDelete: "↔−",
  headerRow: "⊤",
  mergeOrSplit: "⧉",
  undo: "↶",
  redo: "↷",
  left: "▤",
  center: "▥",
  right: "▦",
  justify: "▧",
} as const satisfies Record<string, string> & Record<TextAlignment, string>;

function Separator() {
  return <span aria-hidden="true" className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />;
}

interface ToolbarButtonProps {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function ToolbarButton({ label, active, disabled, onClick, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-7 min-w-7 items-center justify-center rounded px-1.5 text-sm",
        "hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-40",
        "dark:hover:bg-slate-800",
        active && "bg-brand-50 text-brand-700 dark:text-brand-300 dark:bg-slate-800",
      )}
    >
      {children}
    </button>
  );
}
